import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line, LabelList,
} from 'recharts';
import { Gauge } from './Gauge';
import kloudynetLogo from '@assets/image_1777160759976.png';
import { useCustomers } from '../../context/CustomerContext';
import { ExecutiveSummary } from './ExecutiveSummary';
import { MitreCoverageMatrix } from './MitreCoverageMatrix';
import { GeoThreatMap } from './GeoThreatMap';
import {
  InsiderThreatSpotlight,
  type InsiderThreatUser,
} from './InsiderThreatSpotlight';
import { KillChainStories } from './KillChainStories';
import { RuleEffectiveness } from './RuleEffectiveness';
import { ReportFooter } from './ReportView';

// ── Types ────────────────────────────────────────────────────────────────────

export interface MonthlyReportData {
  customerId: string;
  timezone: string;
  /** ISO-2 country code of the customer's home country (used by the threat map). */
  homeCountry?: string;
  /** YYYY-MM (the month being reported on, in customer TZ). */
  month: string;
  prevMonth: string;
  windowStartUtc: string;
  windowEndUtc: string;
  generatedAt: string;
  kpis: {
    /** Classified-only total (excludes Undetermined / unclassified). */
    totalIncidents: number;
    /** The remainder hidden from `totalIncidents` (severityTotal - totalIncidents). */
    undeterminedIncidents: number;
    /** Raw severity sum (used as denominator for the severity-table %). */
    severityTotal: number;
    highSeverityIncidents: number;
    avgTriageMinutes: number | null;
    avgCloseMinutes: number | null;
    highAvgTriageMinutes: number | null;
    highCount: number;
    trueIncidents: number;
    benignIncidents: number;
    falseIncidents: number;
    truePositiveRate: number;
    falsePositiveRate: number;
    prevMonthTotal: number;
    prevMonthHigh: number;
    prevTrueIncidents: number;
    prevBenignIncidents: number;
    prevFalseIncidents: number;
  };
  severity: { severity: string; count: number }[];
  status: { status: string; count: number }[];
  classification: { classification: string; count: number }[];
  prevClassification: { classification: string; count: number }[];
  topIncidents: { title: string; count: number }[];
  mitre: { tactic: string; count: number }[];
  mitreTrend: { tactic: string; current: number; previous: number; change: number; changePercent: number }[];
  topAssets: { host: string; count: number }[];
  threatIntel: { indicatorType: string; count: number }[];
  failedSignIn: { resultType: number; description: string; count: number }[];
  malicious: { user: string; attempts: number; locations: string; failureReasons?: string }[];
  groupMgmt: { operation: string; count: number }[];
  newUsers: { initiator: string; target: string; createdAt: string }[];
  dailyTrend: { day: string; high: number; medium: number; low: number; total: number }[];
  countryThreat: { country: string; count: number }[];
  /**
   * Insider-threat / data-exfiltration spotlight.
   * Optional for backward compatibility with cached/older payloads - newer
   * server responses always include it (with empty `users[]` when no
   * thresholds are crossed and `sourceErrors` flagging any failed sub-query).
   */
  insiderThreat?: {
    users: InsiderThreatUser[];
    sourceErrors: { downloads: boolean; deletes: boolean; afterHours: boolean; oauth: boolean };
  };
  slaPerSeverity: { severity: string; avgMttdMinutes: number | null; avgMttrMinutes: number | null; count: number; closedCount?: number }[];
  /** Per-query failure flag (boolean only - server never leaks raw errors). */
  errors: Record<string, boolean>;
}

interface Props {
  report: MonthlyReportData;
  customerName: string;
}

// ── Palette (mirrors weekly/daily for visual consistency) ────────────────────
const C = {
  primary: '#4c81d1',
  primaryDark: '#2c3e50',
  danger: '#dc3545',
  warning: '#fd7e14',
  success: '#28a745',
  info: '#17a2b8',
  high: '#dc3545',
  medium: '#fd7e14',
  low: '#28a745',
  informational: '#17a2b8',
};

const SEVERITY_COLORS: Record<string, string> = {
  High: C.high,
  Medium: C.medium,
  Low: C.low,
  Informational: C.informational,
};

const STATUS_COLORS: Record<string, string> = {
  Active: C.danger,
  New: C.warning,
  Closed: C.success,
};

const CLASSIFICATION_COLORS: Record<string, string> = {
  TruePositive: C.danger,
  BenignPositive: C.success,
  FalsePositive: C.warning,
  Undetermined: C.info,
};

const TI_COLORS = ['#4c81d1', '#2c3e50', '#dc3545', '#fd7e14', '#28a745', '#17a2b8', '#6c757d'];


// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtMinutes(m: number | null): string {
  if (m === null || !Number.isFinite(m)) return ' - ';
  if (m < 60) return `${m.toFixed(1)} min`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)} hr`;
  const d = h / 24;
  return `${d.toFixed(1)} d`;
}

function triageBadge(m: number | null): { label: string; bg: string } {
  if (m === null) return { label: ' - ', bg: '#6c757d' };
  if (m < 15) return { label: 'EXCELLENT', bg: C.success };
  if (m < 60) return { label: 'GOOD', bg: C.info };
  if (m < 240) return { label: 'NEEDS ATTENTION', bg: C.warning };
  return { label: 'CRITICAL', bg: C.danger };
}

function fmtMonthLong(yyyymm: string): string {
  const [y, m] = yyyymm.split('-').map(Number);
  if (!y || !m) return yyyymm;
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${months[m - 1]} ${y}`;
}

function fmtGeneratedAt(iso: string, tz: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZoneName: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function fmtDelta(curr: number, prev: number): { text: string; tone: 'up' | 'down' | 'flat' } {
  const diff = curr - prev;
  if (diff === 0) return { text: '±0', tone: 'flat' };
  const pct = prev === 0 ? 100 : Math.round((diff / prev) * 100);
  return {
    text: `${diff > 0 ? '▲' : '▼'} ${Math.abs(diff)} (${diff > 0 ? '+' : ''}${pct}%)`,
    tone: diff > 0 ? 'up' : 'down',
  };
}

// ── Subcomponents ────────────────────────────────────────────────────────────

function SectionHeader({ title, tone = 'primary' }: { title: string; tone?: 'primary' | 'danger' | 'warning' | 'success' }) {
  const bg = tone === 'danger'
    ? `linear-gradient(135deg, #7f1d1d 0%, ${C.danger} 100%)`
    : tone === 'warning'
      ? `linear-gradient(135deg, #9a3412 0%, ${C.warning} 100%)`
      : tone === 'success'
        ? `linear-gradient(135deg, #14532d 0%, ${C.success} 100%)`
        : `linear-gradient(135deg, ${C.primaryDark} 0%, ${C.primary} 100%)`;
  return <div className="mr-section-header" style={{ background: bg }}>{title}</div>;
}

function AlertBox({
  variant, title, children,
}: { variant: 'success' | 'warning' | 'danger' | 'info'; title?: string; children: React.ReactNode }) {
  const styles: Record<string, { bg: string; border: string; color: string }> = {
    success: { bg: '#d4edda', border: '#28a745', color: '#155724' },
    warning: { bg: '#fff3cd', border: '#ffc107', color: '#856404' },
    danger:  { bg: '#f8d7da', border: '#dc3545', color: '#721c24' },
    info:    { bg: '#d1ecf1', border: '#17a2b8', color: '#0c5460' },
  };
  const s = styles[variant];
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderLeft: `4px solid ${s.border}`, borderRadius: 8, padding: '12px 16px', margin: '12px 0', color: s.color, fontSize: 14 }}>
      {title && <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>}
      <div>{children}</div>
    </div>
  );
}

function RiskPill({ level }: { level: 'critical' | 'high' | 'medium' | 'low' }) {
  const map = {
    critical: { bg: '#f8d7da', color: '#721c24', label: 'Critical' },
    high:     { bg: '#f8d7da', color: '#721c24', label: 'High' },
    medium:   { bg: '#fff3cd', color: '#856404', label: 'Medium' },
    low:      { bg: '#d4edda', color: '#155724', label: 'Low' },
  } as const;
  const s = map[level];
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', background: s.bg, color: s.color, borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
      {s.label}
    </span>
  );
}

/**
 * Data Reconciliation banner - proves the severity rollup and the classification
 * breakdown both account for the same set of incidents (i.e. no incidents are
 * dropped, double-counted, or land in an unexpected bucket between the two
 * KQL queries).
 *
 * The customer's reference Logic-App report renders the same banner at the
 * top of the Classification section using the formula:
 *
 *   Total (N) = TP (a) + BP (b) + FP (c) + Undetermined (d) + Unclassified (e)
 *
 * When the sum equals the severity total → green ✓ "Verified" banner.
 * When they diverge (rare; would indicate query drift between the two
 * queries) → amber warning showing the absolute and percent difference so
 * an analyst can investigate.
 */
function DataReconciliationBanner({
  severityTotal,
  classification,
  hasError,
}: {
  severityTotal: number;
  classification: { classification: string; count: number }[];
  hasError: boolean;
}) {
  // Skip rendering if either source query failed (would produce a misleading
  // "diverged" banner) or there's no data at all.
  if (hasError || severityTotal === 0 || classification.length === 0) return null;

  const classTotal = classification.reduce((s, c) => s + c.count, 0);
  const matched = classTotal === severityTotal;
  const diff = classTotal - severityTotal;
  const diffPct = severityTotal > 0 ? Math.abs(diff / severityTotal) * 100 : 0;

  // Build the formula breakdown in a stable, customer-recognisable order:
  // TP → BP → FP → Undetermined → Unclassified → any other workspace-emitted bucket.
  const PREFERRED_ORDER = ['TruePositive', 'BenignPositive', 'FalsePositive', 'Undetermined', 'Unclassified'];
  const SHORT_LABELS: Record<string, string> = {
    TruePositive: 'TP',
    BenignPositive: 'BP',
    FalsePositive: 'FP',
    Undetermined: 'Undetermined',
    Unclassified: 'Unclassified',
  };
  const byKey = new Map(classification.map((c) => [c.classification, c.count]));
  const orderedKeys = [
    ...PREFERRED_ORDER.filter((k) => byKey.has(k)),
    ...classification.map((c) => c.classification).filter((k) => !PREFERRED_ORDER.includes(k)),
  ];
  const formulaParts = orderedKeys.map(
    (k) => `${SHORT_LABELS[k] ?? k} (${(byKey.get(k) ?? 0).toLocaleString()})`,
  );

  if (matched) {
    return (
      <AlertBox variant="success" title="✓ Data Reconciliation Verified">
        Total Incidents ({severityTotal.toLocaleString()}) = {formulaParts.join(' + ')}.
      </AlertBox>
    );
  }

  return (
    <AlertBox variant="warning" title="⚠ Data Reconciliation Mismatch">
      Severity total ({severityTotal.toLocaleString()}) does not equal the classification
      breakdown total ({classTotal.toLocaleString()})
      &nbsp; - diff {diff > 0 ? '+' : ''}{diff.toLocaleString()} ({diffPct.toFixed(1)}%).
      &nbsp;Breakdown: {formulaParts.join(' + ')}. This usually indicates the
      severity and classification queries observed slightly different incident
      sets (e.g. an incident was reclassified between the two query executions).
    </AlertBox>
  );
}

function SeverityPill({ severity }: { severity: string }) {
  const key = severity.toLowerCase();
  const colors: Record<string, { bg: string; color: string }> = {
    high:          { bg: '#f8d7da', color: '#721c24' },
    medium:        { bg: '#fff3cd', color: '#856404' },
    low:           { bg: '#d4edda', color: '#155724' },
    informational: { bg: '#d1ecf1', color: '#0c5460' },
  };
  const s = colors[key] ?? { bg: '#e9ecef', color: '#495057' };
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', background: s.bg, color: s.color, borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
      {severity || ' - '}
    </span>
  );
}

function StatusPill({ status }: { status: string }) {
  const key = status.toLowerCase();
  const colors: Record<string, { bg: string; color: string }> = {
    active: { bg: '#f8d7da', color: '#721c24' },
    new:    { bg: '#fff3cd', color: '#856404' },
    closed: { bg: '#d4edda', color: '#155724' },
  };
  const s = colors[key] ?? { bg: '#e9ecef', color: '#495057' };
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', background: s.bg, color: s.color, borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>
      {status || ' - '}
    </span>
  );
}

// ── Main view ────────────────────────────────────────────────────────────────

export function MonthlyReportView({ report, customerName }: Props) {
  const { activeCustomer } = useCustomers();
  const { kpis } = report;
  const triBadge = triageBadge(kpis.avgTriageMinutes);
  const totalDelta = fmtDelta(kpis.totalIncidents, kpis.prevMonthTotal);
  const highDelta = fmtDelta(kpis.highSeverityIncidents, kpis.prevMonthHigh);
  const errorCount = Object.values(report.errors).filter(Boolean).length;

  // Merge current + previous classification rows for the comparison table.
  const classKeys = Array.from(new Set([
    ...report.classification.map((c) => c.classification),
    ...report.prevClassification.map((c) => c.classification),
  ]));
  const classCompare = classKeys.map((k) => {
    const curr = report.classification.find((c) => c.classification === k)?.count ?? 0;
    const prev = report.prevClassification.find((c) => c.classification === k)?.count ?? 0;
    return { classification: k, current: curr, previous: prev, delta: curr - prev };
  }).sort((a, b) => b.current - a.current);

  return (
    <div className="monthly-report-root">
      <style>{`
        .monthly-report-root { font-family: "Segoe UI", system-ui, -apple-system, sans-serif; color: #333; background: #f5f7fa; padding: 24px; border-radius: 16px; }
        .mr-header { background: linear-gradient(135deg, ${C.primaryDark} 0%, ${C.primary} 100%); color: #fff; padding: 28px 32px; border-radius: 16px; margin-bottom: 24px; box-shadow: 0 6px 24px rgba(44,62,80,0.18); }
        .mr-header h1 { margin: 0 0 6px; font-size: 1.7em; letter-spacing: 0.4px; font-weight: 600; }
        .mr-section { background: #fff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.07); margin-bottom: 24px; overflow: hidden; }
        .mr-section-header { color: #fff; padding: 16px 24px; font-size: 1.05em; font-weight: 600; letter-spacing: 0.3px; }
        .mr-section-content { padding: 24px; }
        .mr-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .mr-table th { text-align: left; padding: 10px 12px; background: #f8fafc; color: ${C.primaryDark}; font-weight: 600; border-bottom: 2px solid #e5e7eb; }
        .mr-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #1f2937; vertical-align: top; }
        .mr-table tr:last-child td { border-bottom: none; }
        .mr-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
        .mr-kpi-grid-6 { grid-template-columns: repeat(3, 1fr); }
        @media (min-width: 1280px) { .mr-kpi-grid-6 { grid-template-columns: repeat(6, 1fr); } }
        .mr-kpi-card { background: #fff; border-radius: 14px; padding: 18px 20px; box-shadow: 0 3px 14px rgba(0,0,0,0.07); border-left: 5px solid ${C.primary}; }
        .mr-kpi-card.danger { border-left-color: ${C.danger}; }
        .mr-kpi-card.success { border-left-color: ${C.success}; }
        .mr-kpi-card.warning { border-left-color: ${C.warning}; }
        .mr-kpi-label { font-size: 0.75em; text-transform: uppercase; letter-spacing: 0.6px; color: #6c757d; font-weight: 600; }
        .mr-kpi-value { font-size: 1.85em; font-weight: 700; color: ${C.primaryDark}; margin-top: 6px; line-height: 1.1; }
        .mr-kpi-sub { font-size: 0.78em; color: #6c757d; margin-top: 4px; }
        .mr-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        .mr-grid-2 > div { min-width: 0; }
        .mr-rec-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .mr-mom-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        .mr-mom-grid-3 { grid-template-columns: 1fr 1fr 1fr; }
        @media (max-width: 720px) {
          .mr-mom-grid, .mr-mom-grid-3 { grid-template-columns: 1fr; }
        }
        .mr-rec-card { background: #f8fafc; border-radius: 12px; padding: 16px 18px; border-left: 4px solid ${C.primary}; }
        .mr-rec-card h4 { margin: 0 0 6px; font-size: 0.95em; color: ${C.primaryDark}; font-weight: 700; }
        .mr-rec-card p { margin: 0; font-size: 0.85em; color: #495057; line-height: 1.5; }
        .mr-footer { text-align: center; color: #6c757d; font-size: 0.78em; padding: 18px 0 4px; }
        .mr-no-data { padding: 24px; color: #6c757d; font-size: 0.9em; text-align: center; }
        .mr-error-banner { background: #fff3cd; border: 1px solid #ffc107; border-left: 4px solid #ffc107; color: #856404; padding: 10px 14px; border-radius: 8px; font-size: 12px; margin-bottom: 16px; }
        .mr-delta-up   { color: #c82333; font-weight: 600; }
        .mr-delta-down { color: #218838; font-weight: 600; }
        .mr-delta-flat { color: #6c757d; font-weight: 600; }
        @media (max-width: 720px) {
          .mr-grid-2 { grid-template-columns: 1fr; }
          .mr-kpi-grid { grid-template-columns: repeat(2, 1fr); }
          .mr-rec-grid { grid-template-columns: 1fr; }
        }
        @media print {
          .monthly-report-root { background: #fff; padding: 0; }
          .mr-section { box-shadow: none; border: 1px solid #e5e7eb; page-break-inside: avoid; }
          .mr-header { box-shadow: none; }
        }
      `}</style>

      {/* Gradient header - same banner pattern as Daily / Weekly. */}
      <div className="mr-header">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="rounded-lg bg-white px-4 py-2.5">
            <img src={kloudynetLogo} alt="Kloudynet" style={{ height: 44, display: 'block' }} />
          </div>
          <div className="flex-1 text-center min-w-[240px]">
            <h1 style={{ fontSize: '2.1em', fontWeight: 300, marginBottom: 6, textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
              Monthly Security Operations Report
            </h1>
            <p style={{ opacity: 0.92, fontSize: '1.05em', margin: 0 }}>{customerName}</p>
          </div>
          {activeCustomer?.logoUrl && (
            <div className="rounded-lg bg-white px-4 py-2.5">
              <img src={activeCustomer.logoUrl} alt={customerName} style={{ height: 44, display: 'block' }} />
            </div>
          )}
        </div>
      </div>

      <div className="text-center text-slate-500 italic mb-6 text-sm">
        Reporting Month: {fmtMonthLong(report.month)}
        &nbsp;|&nbsp;
        Compared To: {fmtMonthLong(report.prevMonth)}
        &nbsp;|&nbsp;
        Generated: {fmtGeneratedAt(report.generatedAt, report.timezone)}
      </div>

      <ExecutiveSummary period="monthly" snapshot={report} cacheKey={report.generatedAt} />

      {errorCount > 0 && (
        <div className="mr-error-banner">
          <strong>Partial data:</strong> {errorCount} of{' '}
          {Math.max(1, Object.keys(report.errors).length)} data sources are unavailable.
          Affected sections are flagged individually below. Common causes: the
          ThreatIntelIndicators / SecurityAlert / AuditLogs tables are not provisioned in this
          workspace, or older schemas use different column names.
        </div>
      )}

      {/* KPI strip with month-over-month deltas (6 cards, matching report HTML). */}
      <div className="mr-kpi-grid mr-kpi-grid-6">
        <div className="mr-kpi-card">
          <div className="mr-kpi-label">Total Security Incidents</div>
          <div className="mr-kpi-value">{kpis.totalIncidents.toLocaleString()}</div>
          <div className="mr-kpi-sub">
            vs {kpis.prevMonthTotal.toLocaleString()} last month{' '}
            <span className={`mr-delta-${totalDelta.tone}`}>{totalDelta.text}</span>
          </div>
          {kpis.undeterminedIncidents > 0 && (
            <div className="mr-kpi-sub" style={{ marginTop: 4, fontSize: '0.72em', color: '#94a3b8' }}>
              excludes {kpis.undeterminedIncidents.toLocaleString()} undetermined
            </div>
          )}
        </div>
        <div className="mr-kpi-card danger">
          <div className="mr-kpi-label">High Severity Incidents</div>
          <div className="mr-kpi-value" style={{ color: C.danger }}>{kpis.highSeverityIncidents.toLocaleString()}</div>
          <div className="mr-kpi-sub">
            vs {kpis.prevMonthHigh.toLocaleString()} last month{' '}
            <span className={`mr-delta-${highDelta.tone}`}>{highDelta.text}</span>
          </div>
        </div>
        <div className="mr-kpi-card success">
          <div className="mr-kpi-label">Avg Time to Triage</div>
          <div className="mr-kpi-value">{fmtMinutes(kpis.avgTriageMinutes)}</div>
          <div className="mr-kpi-sub">
            <span style={{ display: 'inline-block', marginTop: 2, padding: '2px 8px', borderRadius: 999, background: triBadge.bg, color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>
              {triBadge.label}
            </span>
          </div>
        </div>
        <div className="mr-kpi-card warning">
          <div className="mr-kpi-label">Avg Time to Close</div>
          <div className="mr-kpi-value">{fmtMinutes(kpis.avgCloseMinutes)}</div>
          <div className="mr-kpi-sub">From creation to closure</div>
        </div>
        <div className="mr-kpi-card success">
          <div className="mr-kpi-label">True Positives</div>
          <div className="mr-kpi-value" style={{ color: C.success }}>{kpis.trueIncidents.toLocaleString()}</div>
          <div className="mr-kpi-sub">
            vs {kpis.prevTrueIncidents.toLocaleString()} last month{' '}
            <span className={`mr-delta-${fmtDelta(kpis.trueIncidents, kpis.prevTrueIncidents).tone}`}>
              {fmtDelta(kpis.trueIncidents, kpis.prevTrueIncidents).text}
            </span>
          </div>
        </div>
        <div className="mr-kpi-card warning">
          <div
            className="mr-kpi-label"
            title="Average time from incident creation to the first analyst action, for High severity incidents only."
          >
            High-Severity Response Time
          </div>
          <div className="mr-kpi-value">{fmtMinutes(kpis.highAvgTriageMinutes)}</div>
          <div className="mr-kpi-sub">
            <span style={{ display: 'inline-block', marginTop: 2, padding: '2px 8px', borderRadius: 999, background: (kpis.highAvgTriageMinutes ?? 99) < 60 ? C.success : C.danger, color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>
              {(kpis.highAvgTriageMinutes ?? 99) < 60 ? 'WITHIN 60-MIN TARGET' : 'OVER 60-MIN TARGET'}
            </span>
          </div>
        </div>
      </div>

      {/* SECTION: Month-over-Month Comparison (mirrors HTML "Feb vs Mar Trend") */}
      <div className="mr-section">
        <SectionHeader title={`${fmtMonthLong(report.prevMonth)} vs ${fmtMonthLong(report.month)} Trend Comparison`} />
        <div className="mr-section-content">
          <div className="mr-mom-grid">
            <MomBar
              title="Total Incidents"
              prev={kpis.prevMonthTotal}
              curr={kpis.totalIncidents}
              prevLabel={fmtMonthLong(report.prevMonth)}
              currLabel={fmtMonthLong(report.month)}
              currColor={kpis.totalIncidents >= kpis.prevMonthTotal ? C.danger : C.success}
            />
            <MomBar
              title="High Severity Incidents"
              prev={kpis.prevMonthHigh}
              curr={kpis.highSeverityIncidents}
              prevLabel={fmtMonthLong(report.prevMonth)}
              currLabel={fmtMonthLong(report.month)}
              currColor={C.danger}
            />
          </div>
          <h4 style={{ color: C.primary, margin: '20px 0 12px', fontSize: '1em', fontWeight: 600 }}>
            Classification Comparison: True Positive / Benign Positive / False Positive
          </h4>
          <div className="mr-mom-grid mr-mom-grid-3">
            <MomBar
              title="True Positives"
              prev={kpis.prevTrueIncidents}
              curr={kpis.trueIncidents}
              prevLabel={fmtMonthLong(report.prevMonth)}
              currLabel={fmtMonthLong(report.month)}
              currColor={C.danger}
              compact
            />
            <MomBar
              title="Benign Positives"
              prev={kpis.prevBenignIncidents}
              curr={kpis.benignIncidents}
              prevLabel={fmtMonthLong(report.prevMonth)}
              currLabel={fmtMonthLong(report.month)}
              currColor={C.success}
              compact
            />
            <MomBar
              title="False Positives"
              prev={kpis.prevFalseIncidents}
              curr={kpis.falseIncidents}
              prevLabel={fmtMonthLong(report.prevMonth)}
              currLabel={fmtMonthLong(report.month)}
              currColor="#9ca3af"
              compact
            />
          </div>
          <AlertBox variant="info" title="Trend Analysis">
            Comparing {fmtMonthLong(report.month)} vs {fmtMonthLong(report.prevMonth)} helps identify
            emerging threats. <strong style={{ color: C.danger }}>▲ Increases</strong> require attention.{' '}
            <strong style={{ color: C.success }}>▼ Decreases</strong> may indicate successful mitigations.
          </AlertBox>
        </div>
      </div>

      {/* SECTION: Response Performance */}
      <div className="mr-section">
        <SectionHeader title="Response Performance Dashboard" />
        <div className="mr-section-content">
          {report.errors['metrics'] && (
            <AlertBox variant="warning" title="Query unavailable">
              The triage / closure query failed against the customer's workspace. Numbers below
              fall back to zero and should not be interpreted as actual SOC performance.
            </AlertBox>
          )}
          <div className="mr-grid-2">
            <Gauge
              value={kpis.avgTriageMinutes}
              unit="min"
              label="Avg Triage Time"
              bands={[15, 30, 60]}
              max={60}
              dtick={15}
              thresholdValue={60}
            />
            <Gauge
              value={kpis.highAvgTriageMinutes}
              unit="min"
              label="High-Severity Response Time"
              bands={[15, 30, 60]}
              max={60}
              dtick={15}
              thresholdValue={60}
            />
          </div>
          {kpis.highAvgTriageMinutes !== null && (() => {
            // Title + variant track the actual benchmark performance - we used
            // to always say "Excellent" which was misleading whenever the team
            // missed the 60-minute target.
            const underTarget = (kpis.highAvgTriageMinutes ?? 99) < 60;
            return (
              <AlertBox
                variant={underTarget ? 'success' : 'warning'}
                title={underTarget ? 'Excellent Response Performance' : 'High-Severity Response Time Above Target'}
              >
                The SOC team responded to <strong>{kpis.highCount.toLocaleString()} high severity incidents</strong>{' '}
                with an average response time of <strong>{fmtMinutes(kpis.highAvgTriageMinutes)}</strong>.{' '}
                {underTarget
                  ? <>This is comfortably inside the 60-minute industry best-practice target.</>
                  : <>This is above the 60-minute industry best-practice target - review the on-call schedule and automated response procedures.</>}
              </AlertBox>
            );
          })()}
        </div>
      </div>

      {/* SECTION: Daily Incident Trend */}
      <div className="mr-section">
        <SectionHeader title="Daily Incident Trend" />
        <div className="mr-section-content">
          {report.errors['dailyTrend'] && (
            <AlertBox variant="warning" title="Query unavailable">
              The daily-trend query failed; this section shows no data.
            </AlertBox>
          )}
          {report.dailyTrend.length === 0 ? (
            <div className="mr-no-data">
              {report.errors['dailyTrend'] ? 'Data unavailable.' : 'No incidents in this month.'}
            </div>
          ) : (
            <div style={{ height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={report.dailyTrend} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day" fontSize={10} angle={-30} textAnchor="end" interval={Math.max(0, Math.floor(report.dailyTrend.length / 12))} height={60} />
                  <YAxis fontSize={11} />
                  <RTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="high" stroke={C.high} strokeWidth={2} dot={false} name="High" />
                  <Line type="monotone" dataKey="medium" stroke={C.medium} strokeWidth={2} dot={false} name="Medium" />
                  <Line type="monotone" dataKey="low" stroke={C.low} strokeWidth={2} dot={false} name="Low" />
                  <Line type="monotone" dataKey="total" stroke={C.primaryDark} strokeWidth={2} strokeDasharray="4 4" dot={false} name="Total" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* SECTION: Incident Analysis (severity + status side-by-side) */}
      <div className="mr-section">
        <SectionHeader title="Incident Analysis" />
        <div className="mr-section-content">
          {(report.errors['severity'] || report.errors['status']) && (
            <AlertBox variant="warning" title="Query unavailable">
              {[report.errors['severity'] && 'severity', report.errors['status'] && 'status']
                .filter(Boolean).join(' & ')} query failed; affected pane(s) below show no data.
            </AlertBox>
          )}
          <div className="mr-grid-2">
            <div>
              <h3 style={{ color: C.primaryDark, marginTop: 0, marginBottom: 12, fontSize: '1em' }}>By Severity</h3>
              <SeverityTable rows={report.severity} classifiedTotal={kpis.totalIncidents} />
              <div style={{ height: 220, marginTop: 12 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={report.severity} dataKey="count" nameKey="severity" innerRadius={50} outerRadius={85} paddingAngle={2}>
                      {report.severity.map((row, i) => (
                        <Cell key={i} fill={SEVERITY_COLORS[row.severity] ?? '#6c757d'} />
                      ))}
                    </Pie>
                    <RTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <h3 style={{ color: C.primaryDark, marginTop: 0, marginBottom: 12, fontSize: '1em' }}>By Status</h3>
              <StatusTable rows={report.status} />
              <div style={{ height: 220, marginTop: 12 }}>
                <ResponsiveContainer>
                  <BarChart data={report.status} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="status" fontSize={11} />
                    <YAxis fontSize={11} />
                    <RTooltip />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {report.status.map((row, i) => (
                        <Cell key={i} fill={STATUS_COLORS[row.status] ?? C.primary} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION: Classification (current vs previous month) */}
      <div className="mr-section">
        <SectionHeader title="Incident Classification - Month over Month" />
        <div className="mr-section-content">
          {(report.errors['classification'] || report.errors['prevClassification']) && (
            <AlertBox variant="warning" title="Query unavailable">
              The classification query failed; comparison may be incomplete.
            </AlertBox>
          )}
          {/* Data reconciliation banner - proves no incidents are dropped or
              double-counted between the severity rollup and the classification
              breakdown. Mirrors the customer's reference report banner. */}
          <DataReconciliationBanner
            severityTotal={report.kpis.severityTotal}
            classification={report.classification}
            hasError={!!report.errors['classification'] || !!report.errors['severity']}
          />

          {/* Detection-quality KPIs (TP / FP rate). Mirrors the HTML report's
              "Detection Metrics" callout. Both rates use the classified total
              (excluding Undetermined) as the denominator. */}
          <div className="mr-grid-2" style={{ marginBottom: 16 }}>
            <div
              style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '14px 18px' }}
              title="Share of classified incidents that turned out to be real threats."
            >
              <div style={{ fontSize: '0.75em', color: '#047857', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                True Positive Rate (real threats)
              </div>
              <div style={{ fontSize: '1.65em', fontWeight: 700, color: '#065f46', marginTop: 4 }}>
                {kpis.truePositiveRate.toFixed(1)}%
              </div>
              <div style={{ fontSize: '0.78em', color: '#047857', marginTop: 4 }}>
                {kpis.trueIncidents.toLocaleString()} confirmed real threats out of {kpis.totalIncidents.toLocaleString()} classified incidents
              </div>
            </div>
            <div
              style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '14px 18px' }}
              title="Share of classified incidents that turned out to be false alarms."
            >
              <div style={{ fontSize: '0.75em', color: '#b91c1c', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                False Positive Rate (false alarms)
              </div>
              <div style={{ fontSize: '1.65em', fontWeight: 700, color: '#7f1d1d', marginTop: 4 }}>
                {kpis.falsePositiveRate.toFixed(2)}%
              </div>
              <div style={{ fontSize: '0.78em', color: '#b91c1c', marginTop: 4 }}>
                {kpis.falseIncidents.toLocaleString()} false alarms out of {kpis.totalIncidents.toLocaleString()} classified incidents
              </div>
            </div>
          </div>
          {classCompare.length === 0 ? (
            <div className="mr-no-data">No classified incidents in this period.</div>
          ) : (
            <div className="mr-grid-2">
              <div>
                <table className="mr-table">
                  <thead>
                    <tr>
                      <th>Classification</th>
                      <th style={{ textAlign: 'center' }}>{fmtMonthLong(report.month)}</th>
                      <th style={{ textAlign: 'center' }}>{fmtMonthLong(report.prevMonth)}</th>
                      <th style={{ textAlign: 'center' }}>Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classCompare.map((row) => {
                      const tone = row.delta > 0 ? 'up' : row.delta < 0 ? 'down' : 'flat';
                      const sign = row.delta > 0 ? '+' : '';
                      return (
                        <tr key={row.classification}>
                          <td>{row.classification}</td>
                          <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.current.toLocaleString()}</td>
                          <td style={{ textAlign: 'center' }}>{row.previous.toLocaleString()}</td>
                          <td style={{ textAlign: 'center' }} className={`mr-delta-${tone}`}>{sign}{row.delta}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={report.classification} dataKey="count" nameKey="classification" outerRadius={100} label>
                      {report.classification.map((row, i) => (
                        <Cell key={i} fill={CLASSIFICATION_COLORS[row.classification] ?? TI_COLORS[i % TI_COLORS.length]} />
                      ))}
                    </Pie>
                    <RTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SECTION: Top 10 Security Incidents */}
      <div className="mr-section">
        <SectionHeader title="Top 10 Security Incidents" />
        <div className="mr-section-content">
          {report.errors['topIncidents'] && (
            <AlertBox variant="warning" title="Query unavailable">
              The top-incidents query failed; this section shows no data.
            </AlertBox>
          )}
          {report.topIncidents.length === 0 ? (
            <div className="mr-no-data">
              {report.errors['topIncidents'] ? 'Data unavailable.' : 'No incidents in this period.'}
            </div>
          ) : (
            <>
              <div style={{ height: 320, marginBottom: 16 }}>
                <ResponsiveContainer>
                  <BarChart data={report.topIncidents} margin={{ top: 8, right: 24, left: 0, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="title"
                      fontSize={10}
                      angle={-30}
                      textAnchor="end"
                      interval={0}
                      tickFormatter={(t: string) => (t.length > 30 ? t.slice(0, 28) + '…' : t)}
                    />
                    <YAxis fontSize={11} />
                    <RTooltip />
                    <Bar dataKey="count" fill={C.primary} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <table className="mr-table">
                <thead><tr><th>Incident Type</th><th style={{ textAlign: 'center' }}>Count</th><th style={{ textAlign: 'center' }}>Priority</th></tr></thead>
                <tbody>
                  {report.topIncidents.map((row, i) => (
                    <tr key={i}>
                      <td>{row.title}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
                      <td style={{ textAlign: 'center' }}><RiskPill level="critical" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {/* SECTION: SLA per Severity */}
      <div className="mr-section">
        <SectionHeader title="Response Targets by Severity" />
        <div className="mr-section-content">
          {report.errors['slaPerSeverity'] && (
            <AlertBox variant="warning" title="Query unavailable">
              The response-time-by-severity query failed; this section shows no data.
            </AlertBox>
          )}
          {report.slaPerSeverity.length === 0 ? (
            <div className="mr-no-data">
              {report.errors['slaPerSeverity'] ? 'Data unavailable.' : 'No incidents in this period.'}
            </div>
          ) : (
            <table className="mr-table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th
                    style={{ textAlign: 'center' }}
                    title="Average Detection Time - how long from when an event happened to when an alert was raised."
                  >
                    Avg Detection Time
                  </th>
                  <th
                    style={{ textAlign: 'center' }}
                    title="Average Resolution Time - how long from when an incident was created to when it was closed."
                  >
                    Avg Resolution Time
                  </th>
                  <th style={{ textAlign: 'center' }}>Incident Count</th>
                </tr>
              </thead>
              <tbody>
                {report.slaPerSeverity.map((row, i) => (
                  <tr key={i}>
                    <td><SeverityPill severity={row.severity} /></td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{fmtMinutes(row.avgMttdMinutes)}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{fmtMinutes(row.avgMttrMinutes)}</td>
                    <td style={{ textAlign: 'center' }}>{row.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* SECTION: MITRE Tactics + month-over-month trend */}
      <div className="mr-section">
        <SectionHeader title="Top 10 Attack Tactics (MITRE ATT&CK) - Month over Month" tone="warning" />
        <div className="mr-section-content">
          <AlertBox variant="warning" title="Primary Threat Vector">
            Focus defensive measures on the most prevalent attack tactics observed during this reporting period.
          </AlertBox>
          {(report.errors['mitre'] || report.errors['mitreTrend']) && (
            <AlertBox variant="warning" title="Query unavailable">
              The attack-tactic query failed; trend data may be incomplete.
            </AlertBox>
          )}
          {/* Coverage matrix headlines the section: shows the full 14 enterprise
              tactics including zero-activity gaps so coverage is visible. */}
          <MitreCoverageMatrix
            title="ATT&CK Coverage (This Month)"
            periodLabel="this month"
            data={report.mitre}
          />
          {report.mitre.length === 0 && report.mitreTrend.length === 0 ? (
            <div className="mr-no-data">No incidents tagged with an attack tactic in this period.</div>
          ) : (
            <>
              {report.mitre.length > 0 && (
                <div style={{ height: Math.max(240, report.mitre.length * 32 + 60), marginBottom: 16 }}>
                  <ResponsiveContainer>
                    <BarChart data={[...report.mitre].reverse()} layout="vertical" margin={{ top: 8, right: 24, left: 100, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" fontSize={11} />
                      <YAxis type="category" dataKey="tactic" fontSize={11} width={140} />
                      <RTooltip />
                      <Bar dataKey="count" fill={C.warning} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {report.mitreTrend.length > 0 && (
                <table className="mr-table">
                  <thead>
                    <tr>
                      <th>Tactic</th>
                      <th style={{ textAlign: 'center' }}>{fmtMonthLong(report.month)}</th>
                      <th style={{ textAlign: 'center' }}>{fmtMonthLong(report.prevMonth)}</th>
                      <th style={{ textAlign: 'center' }}>Δ</th>
                      <th style={{ textAlign: 'center' }}>Δ%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.mitreTrend.map((row, i) => {
                      const tone = row.change > 0 ? 'up' : row.change < 0 ? 'down' : 'flat';
                      const sign = row.change > 0 ? '+' : '';
                      const pctSign = row.changePercent > 0 ? '+' : '';
                      return (
                        <tr key={i}>
                          <td>{row.tactic}</td>
                          <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.current.toLocaleString()}</td>
                          <td style={{ textAlign: 'center' }}>{row.previous.toLocaleString()}</td>
                          <td style={{ textAlign: 'center' }} className={`mr-delta-${tone}`}>{sign}{row.change}</td>
                          <td style={{ textAlign: 'center' }} className={`mr-delta-${tone}`}>{pctSign}{row.changePercent.toFixed(1)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      {/* SECTION: Top Targeted Assets */}
      <div className="mr-section">
        <SectionHeader title="Top 5 Targeted Assets" tone="danger" />
        <div className="mr-section-content">
          {report.errors['topAssets'] && (
            <AlertBox variant="warning" title="Query unavailable">
              The targeted-assets query failed (typically the SecurityAlert table is not provisioned
              or its Entities column schema differs in this workspace).
            </AlertBox>
          )}
          {report.topAssets.length === 0 ? (
            <div className="mr-no-data">
              {report.errors['topAssets'] ? 'Data unavailable.' : 'No host-targeted alerts recorded.'}
            </div>
          ) : (
            <table className="mr-table">
              <thead>
                <tr>
                  <th>Host</th>
                  <th style={{ textAlign: 'center' }}>Alert Count</th>
                  <th style={{ textAlign: 'center' }}>Risk</th>
                </tr>
              </thead>
              <tbody>
                {report.topAssets.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'ui-monospace, monospace' }}>{row.host}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
                    <td style={{ textAlign: 'center' }}><RiskPill level="critical" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* SECTION: Threat Intelligence */}
      <div className="mr-section">
        <SectionHeader title="Threat Intelligence Overview" />
        <div className="mr-section-content">
          {report.errors['threatIntel'] && (
            <AlertBox variant="warning" title="Query unavailable">
              The ThreatIntelIndicators table query failed (most likely the table is not provisioned
              in this workspace, or its schema differs).
            </AlertBox>
          )}
          {report.threatIntel.length === 0 ? (
            <div className="mr-no-data">
              {report.errors['threatIntel']
                ? 'Data unavailable.'
                : 'No active threat-intelligence indicators in the workspace.'}
            </div>
          ) : (
            <div className="mr-grid-2">
              <div>
                <table className="mr-table">
                  <thead><tr><th>Indicator Type</th><th style={{ textAlign: 'center' }}>Count</th></tr></thead>
                  <tbody>
                    {report.threatIntel.map((row, i) => (
                      <tr key={i}>
                        <td>{row.indicatorType}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ height: 320 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={report.threatIntel} dataKey="count" nameKey="indicatorType" outerRadius={110} label={(p: { indicatorType?: string }) => p.indicatorType ?? ''}>
                      {report.threatIntel.map((_, i) => (
                        <Cell key={i} fill={TI_COLORS[i % TI_COLORS.length]} />
                      ))}
                    </Pie>
                    <RTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SECTION: User Activity Analysis */}
      <div className="mr-section">
        <SectionHeader title="User Activity Analysis" tone="danger" />
        <div className="mr-section-content">
          <h3 style={{ color: C.danger, margin: '0 0 12px', fontSize: '1em' }}>
            Top 5 Failed Sign-in Analysis
            {report.errors['failedSignIn'] && (
              <span style={{ marginLeft: 10, fontSize: 11, color: '#856404', background: '#fff3cd', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>Query unavailable</span>
            )}
          </h3>
          {report.failedSignIn.length === 0 ? (
            <div className="mr-no-data">
              {report.errors['failedSignIn'] ? 'Data unavailable.' : 'No failed sign-ins recorded.'}
            </div>
          ) : (
            <table className="mr-table">
              <thead>
                <tr>
                  <th>Error Code</th>
                  <th style={{ textAlign: 'center' }}>Count</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'center' }}>Risk</th>
                </tr>
              </thead>
              <tbody>
                {report.failedSignIn.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'ui-monospace, monospace' }}>{row.resultType}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
                    <td style={{ fontSize: 12, color: '#475569' }}>{row.description}</td>
                    <td style={{ textAlign: 'center' }}><RiskPill level="critical" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ color: C.danger, margin: '28px 0 12px', fontSize: '1em' }}>
            Top 10 Malicious Sign-in Attempts
            {report.errors['malicious'] && (
              <span style={{ marginLeft: 10, fontSize: 11, color: '#856404', background: '#fff3cd', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>Query unavailable</span>
            )}
          </h3>
          {report.malicious.length === 0 ? (
            <div className="mr-no-data">
              {report.errors['malicious']
                ? 'Data unavailable.'
                : 'No malicious sign-in attempts (IP-blocked) recorded.'}
            </div>
          ) : (
            <table className="mr-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th style={{ textAlign: 'center' }}>Attempts</th>
                  <th>Locations</th>
                  <th style={{ textAlign: 'center' }}>Risk</th>
                </tr>
              </thead>
              <tbody>
                {report.malicious.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{row.user}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.attempts.toLocaleString()}</td>
                    <td style={{ fontSize: 12, color: '#475569' }}>{row.locations}</td>
                    <td style={{ textAlign: 'center' }}><RiskPill level="critical" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* ── Geographic Threat Map ───────────────────────────────────── */}
          <div style={{ margin: '24px 0 8px' }}>
            <GeoThreatMap
              title="Threat Origin Map (This Month)"
              subtitle="Country sources of malicious sign-in attempts (failed-credential events)."
              homeCountry={report.homeCountry}
              data={report.countryThreat}
            />
          </div>

          <h3 style={{ color: C.warning, margin: '28px 0 12px', fontSize: '1em' }}>
            Malicious Sign-ins by Country
            {report.errors['countryThreat'] && (
              <span style={{ marginLeft: 10, fontSize: 11, color: '#856404', background: '#fff3cd', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>Query unavailable</span>
            )}
          </h3>
          {report.countryThreat.length === 0 ? (
            <div className="mr-no-data">
              {report.errors['countryThreat'] ? 'Data unavailable.' : 'No country-attributed malicious sign-ins recorded.'}
            </div>
          ) : (
            <div style={{ height: Math.max(220, Math.min(report.countryThreat.length, 12) * 28 + 40) }}>
              <ResponsiveContainer>
                <BarChart data={report.countryThreat.slice(0, 12)} layout="vertical" margin={{ top: 8, right: 24, left: 80, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" fontSize={11} />
                  <YAxis type="category" dataKey="country" fontSize={11} width={120} />
                  <RTooltip />
                  <Bar dataKey="count" fill={C.warning} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* SECTION: Insider Threat & Data-Exfiltration Spotlight */}
      <div className="mr-section">
        <SectionHeader title="Insider Threat & Data-Exfiltration Spotlight" tone="danger" />
        <div className="mr-section-content">
          <InsiderThreatSpotlight
            users={report.insiderThreat?.users ?? []}
            periodLabel="this month"
            errors={report.insiderThreat?.sourceErrors}
          />
        </div>
      </div>

      {/* SECTION: Top 3 Incidents as Kill-Chain Stories (AI-narrated) */}
      <div className="mr-section">
        <SectionHeader title="Top 3 Incidents - Kill-Chain Walkthroughs" tone="danger" />
        <div className="mr-section-content">
          <KillChainStories
            customerId={report.customerId}
            period="monthly"
            month={report.month}
            cacheKey={report.generatedAt}
          />
        </div>
      </div>

      {/* SECTION: Detection-Rule Effectiveness Scorecard */}
      <div className="mr-section">
        <SectionHeader title="Detection-Rule Effectiveness" />
        <div className="mr-section-content">
          <RuleEffectiveness
            customerId={report.customerId}
            period="monthly"
            month={report.month}
            cacheKey={report.generatedAt}
          />
        </div>
      </div>

      {/* SECTION: Identity Lifecycle */}
      <div className="mr-section">
        <SectionHeader title="Identity Lifecycle &amp; Access Changes" />
        <div className="mr-section-content">
          <h3 style={{ color: C.primaryDark, margin: '0 0 12px', fontSize: '1em' }}>
            Top 10 Group Management Operations
            {report.errors['groupMgmt'] && (
              <span style={{ marginLeft: 10, fontSize: 11, color: '#856404', background: '#fff3cd', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>Query unavailable</span>
            )}
          </h3>
          {report.groupMgmt.length === 0 ? (
            <div className="mr-no-data">
              {report.errors['groupMgmt'] ? 'Data unavailable.' : 'No group-management activity recorded.'}
            </div>
          ) : (
            <table className="mr-table">
              <thead>
                <tr><th>Operation</th><th style={{ textAlign: 'center' }}>Count</th></tr>
              </thead>
              <tbody>
                {report.groupMgmt.map((row, i) => (
                  <tr key={i}>
                    <td>{row.operation}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ color: C.primaryDark, margin: '28px 0 12px', fontSize: '1em' }}>
            New User Accounts (Top 10)
            {report.errors['newUsers'] && (
              <span style={{ marginLeft: 10, fontSize: 11, color: '#856404', background: '#fff3cd', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>Query unavailable</span>
            )}
          </h3>
          {report.newUsers.length === 0 ? (
            <div className="mr-no-data">
              {report.errors['newUsers'] ? 'Data unavailable.' : 'No new user accounts created in this period.'}
            </div>
          ) : (
            <table className="mr-table">
              <thead>
                <tr>
                  <th>Initiator</th>
                  <th>Target</th>
                  <th style={{ textAlign: 'center' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {report.newUsers.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{row.initiator}</td>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{row.target}</td>
                    <td style={{ textAlign: 'center', fontSize: 12, color: '#475569' }}>{row.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Incident Detail section intentionally removed (per March 2026 report
          template - the report focuses on aggregates and the per-incident view
          lives in the main Incidents tab). */}

      {/* SECTION: Recommendations (dynamic - derived from the report's own
          top MITRE tactic, top targeted asset, and headline KPIs so the
          insights track each customer/month rather than reciting boilerplate). */}
      <div className="mr-section">
        <SectionHeader title="Insights & Recommendations" tone="success" />
        <div className="mr-section-content">
          <div className="mr-rec-grid">
            <div className="mr-rec-card">
              <h4>Incident Volume</h4>
              <p>
                <strong>{kpis.totalIncidents.toLocaleString()}</strong> classified incidents
                {kpis.undeterminedIncidents > 0 && (
                  <> (excludes {kpis.undeterminedIncidents.toLocaleString()} that were never assigned a verdict)</>
                )}{' '}
                with <strong>{kpis.highSeverityIncidents.toLocaleString()}</strong> high-severity events.{' '}
                {kpis.totalIncidents > kpis.prevMonthTotal
                  ? <>Volume is up vs {fmtMonthLong(report.prevMonth)} - tune the incident-grouping rules to reduce alert noise.</>
                  : <>Volume is flat or down vs {fmtMonthLong(report.prevMonth)} - current detection tuning is holding.</>}
              </p>
            </div>
            <div className="mr-rec-card" style={{ borderLeftColor: C.success }}>
              <h4>Response Time</h4>
              <p>
                Average response time <strong>{fmtMinutes(kpis.avgTriageMinutes)}</strong>; for high-severity
                incidents <strong>{fmtMinutes(kpis.highAvgTriageMinutes)}</strong>.{' '}
                {(kpis.highAvgTriageMinutes ?? 99) < 60
                  ? <>Comfortably inside the 60-minute industry best-practice target.</>
                  : <>High-severity response time is over the 60-minute target - review the on-call schedule and automated response procedures.</>}
              </p>
            </div>
            <div className="mr-rec-card" style={{ borderLeftColor: C.danger }}>
              <h4>Primary Threat</h4>
              <p>
                {report.mitre.length > 0 ? (
                  <>
                    Most-seen attack tactic this month (MITRE ATT&amp;CK): <strong>{report.mitre[0]!.tactic}</strong>{' '}
                    ({report.mitre[0]!.count.toLocaleString()} incidents). Prioritise detection coverage,
                    threat-hunting, and conditional-access controls that target this tactic.
                  </>
                ) : (
                  <>No attack-tactic data was returned this period - verify incidents include the MITRE
                    ATT&amp;CK tactic field in the workspace.</>
                )}
              </p>
            </div>
            <div className="mr-rec-card" style={{ borderLeftColor: C.warning }}>
              <h4>Asset Protection</h4>
              <p>
                {report.topAssets.length > 0 ? (
                  <>
                    <strong>{report.topAssets[0]!.host}</strong> needs immediate attention
                    ({report.topAssets[0]!.count.toLocaleString()} attacks).
                    Patch the operating system / firmware to current vendor recommendations,
                    rotate credentials, and confirm the device is fully onboarded to Microsoft
                    Defender and Microsoft Sentinel.
                  </>
                ) : (
                  <>No targeted-asset data this period - confirm that incident enrichment is producing
                    related rule IDs and entity details for the workspace.</>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      <ReportFooter generatedAt={report.generatedAt} timezone={report.timezone} />
    </div>
  );
}

// ── Month-over-month bar comparison ──────────────────────────────────────────

/**
 * Compact two-bar (prev vs curr) horizontal comparison used in the MoM
 * trend section. Renders as a tiny standalone Recharts BarChart with the
 * delta % rendered above. We deliberately avoid grouping all metrics into
 * one chart because each metric has wildly different magnitudes (Total ~5k,
 * False Positives ~80) and a shared y-axis would make the small bars
 * unreadable.
 */
function MomBar({
  title,
  prev,
  curr,
  prevLabel,
  currLabel,
  currColor,
  compact = false,
}: {
  title: string;
  prev: number;
  curr: number;
  prevLabel: string;
  currLabel: string;
  currColor: string;
  compact?: boolean;
}) {
  const data = [
    { month: prevLabel, value: prev, fill: '#9ca3af' },
    { month: currLabel, value: curr, fill: currColor },
  ];
  const delta = fmtDelta(curr, prev);
  const height = compact ? 160 : 200;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: '0.85em', fontWeight: 600, color: C.primaryDark }}>{title}</div>
        <div className={`mr-delta-${delta.tone}`} style={{ fontSize: '0.78em', fontWeight: 700 }}>
          {delta.text}
        </div>
      </div>
      <div style={{ height }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 18, right: 12, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
            <YAxis tick={{ fontSize: 10, fill: '#64748b' }} allowDecimals={false} />
            <RTooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} formatter={(v: number) => v.toLocaleString()} />
            <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={70}>
              <LabelList dataKey="value" position="top" fontSize={11} fontWeight={700} formatter={(v: any) => Number(v).toLocaleString()} />
              {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Small inline tables ──────────────────────────────────────────────────────

function SeverityTable({
  rows,
  classifiedTotal,
}: {
  rows: { severity: string; count: number }[];
  classifiedTotal: number;
}) {
  if (rows.length === 0) return <div className="mr-no-data">No incidents.</div>;
  return (
    <table className="mr-table">
      <thead>
        <tr>
          <th>Severity</th>
          <th style={{ textAlign: 'center' }}>Count</th>
          <th style={{ textAlign: 'center', width: 90 }} title="Severity count as a percentage of classified incidents (TP+BP+FP). Can exceed 100% because severity counts every incident, including those with Undetermined classification.">
            % of classified
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const pct = classifiedTotal > 0 ? (row.count / classifiedTotal) * 100 : 0;
          return (
            <tr key={row.severity}>
              <td><SeverityPill severity={row.severity} /></td>
              <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
              <td style={{ textAlign: 'center', color: '#475569' }}>{pct.toFixed(1)}%</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function StatusTable({ rows }: { rows: { status: string; count: number }[] }) {
  if (rows.length === 0) return <div className="mr-no-data">No incidents.</div>;
  return (
    <table className="mr-table">
      <thead><tr><th>Status</th><th style={{ textAlign: 'center' }}>Count</th></tr></thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.status}>
            <td><StatusPill status={row.status} /></td>
            <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
